import React, { useState, useEffect } from 'react';
import { ReactComponent as CloseIcon } from '../../../assets/icons/x-icon.svg';
import { ReactComponent as ArrowLeftIcon } from '../../../assets/icons/arrow-left-icon.svg';
import { ReactComponent as ArrowRightIcon } from '../../../assets/icons/arrow-right-icon.svg';
import './CalendarModal.css';
import { formatToReadableDate } from '../../../utils/utils';

const MONTH_NAMES = [
  "Янв", "Фев", "Мар", "Апр", "Май", "Июн",
  "Июл", "Авг", "Сен", "Окт", "Ноя", "Дек"
];

const toYearMonth = (dateStr) => {
  if (!dateStr) return null;
  return dateStr.slice(0, 7);
};

// Add N months to a "YYYY-MM" string
const addMonths = (ym, n) => {
  const [y, m] = ym.split('-').map(Number);
  const d = new Date(y, m - 1 + n, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const CalendarModal = ({ title, isOpen, closeModal, dateRange, applyDateRangeFilter, allowSameMonth = false, limit = 24, minDate, maxDate }) => {
  const getInitialYear = () => {
    const d = dateRange?.startDate;
    return d ? parseInt(d.slice(0, 4)) : new Date().getFullYear();
  };

  const [viewYear, setViewYear] = useState(getInitialYear);
  const [startMonth, setStartMonth] = useState(() => toYearMonth(dateRange?.startDate));
  const [endMonth, setEndMonth] = useState(() => toYearMonth(dateRange?.endDate));
  // 'start' = user is picking start date, 'end' = picking end date
  const [activeField, setActiveField] = useState('start');
  const [hoverMonth, setHoverMonth] = useState(null);
  const [slideDir, setSlideDir] = useState('none');

  const changeYear = (next) => {
    if (next === viewYear) return;
    setSlideDir(next > viewYear ? 'left' : 'right');
    setViewYear(next);
  };

  // Sync calendar view to the active field's date
  useEffect(() => {
    const anchor = activeField === 'start' ? startMonth : (endMonth || startMonth);
    if (anchor) {
      const next = parseInt(anchor.slice(0, 4));
      changeYear(next);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeField, startMonth, endMonth]);

  if (!isOpen) return null;

  const handleMonthClick = (monthIndex) => {
    const ym = `${viewYear}-${String(monthIndex + 1).padStart(2, '0')}`;

    if (activeField === 'start') {
      setStartMonth(ym);
      // If new start is after current end, clear end
      if (endMonth && ym >= endMonth) setEndMonth(null);
      // If limit is set and current end exceeds limit from new start, clear end
      if (limit && endMonth && endMonth > addMonths(ym, limit)) setEndMonth(null);
      // setTimeout(() => setActiveField('end'), 0);
      setActiveField('end')
    } else {
      setEndMonth(ym);
      // If new end is before current start, clear start
      if (startMonth && ym <= startMonth) setStartMonth(null);
    }
  };

  const minYear = minDate ? parseInt(minDate.split('-')[0]) : null;
  const maxYear = maxDate ? parseInt(maxDate.split('-')[0]) : null;
  const prevYearDisabled = minYear !== null && viewYear <= minYear;
  const nextYearDisabled = maxYear !== null && viewYear >= maxYear;

  const isDisabled = (ym) => {
    if (minDate && ym < minDate) return true;
    if (maxDate && ym > maxDate) return true;
    if (activeField === 'end' && startMonth) {
      if (!allowSameMonth && ym <= startMonth) return true;
      if (allowSameMonth && ym < startMonth) return true;
      if (limit && ym > addMonths(startMonth, limit)) return true;
    }
    return false;
  };

  const handleApply = () => {
    applyDateRangeFilter({ startDate: startMonth, endDate: endMonth });
    closeModal();
  };

  return (
    <div className="calendar-modal modal-overlay" onClick={() => closeModal()}>
      <div className="modal-container" style={{ minWidth: "320px" }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h1>{title}</h1>
          <button
            onClick={closeModal}
            className='modal-header-close-button'
            onMouseEnter={(e) => (e.currentTarget.style.color = "#4b5563")}
            onMouseLeave={(e) => (e.currentTarget.style.color = "#9ca3af")}
          >
            <CloseIcon size={24} />
          </button>
        </div>

        <div className="range-display">
          <div
            className={`range-item ${activeField === 'start' ? 'range-item-active' : ''}`}
            onClick={() => setActiveField('start')}
          >
            <span className={`label ${activeField === 'start' ? 'range-label-active' : ''}`}>Начало</span>
            <span className="date-value">{formatToReadableDate(startMonth) || "-"}</span>
          </div>
          <div
            className={`range-item ${activeField === 'end' ? 'range-item-active' : ''}`}
            onClick={() => setActiveField('end')}
          >
            <span className={`label ${activeField === 'end' ? 'range-label-active' : ''}`}>Конец</span>
            <span className="date-value">{formatToReadableDate(endMonth) || "-"}</span>
          </div>
        </div>

        <div className="calendar-element">
          <div className="calendar-header">
            <button className="calendar-nav-button" onClick={() => !prevYearDisabled && changeYear(viewYear - 1)} disabled={prevYearDisabled} style={{ opacity: prevYearDisabled ? 0.35 : 1, cursor: prevYearDisabled ? 'not-allowed' : 'pointer' }}><ArrowLeftIcon size={12} color="#6D6D6D" /></button>
            <h3 className="month-year">{viewYear}</h3>
            <button className="calendar-nav-button" onClick={() => !nextYearDisabled && changeYear(viewYear + 1)} disabled={nextYearDisabled} style={{ opacity: nextYearDisabled ? 0.35 : 1, cursor: nextYearDisabled ? 'not-allowed' : 'pointer' }}><ArrowRightIcon size={12} color="#6D6D6D" /></button>
          </div>

          <div key={viewYear} className={`months-grid slide-${slideDir}`} onMouseLeave={() => setHoverMonth(null)}>
            {(() => {
              const today = new Date();
              const todayYear = today.getFullYear();
              const todayMonth = today.getMonth();
              const showPreview = activeField === 'end' && startMonth && hoverMonth && hoverMonth > startMonth;
              return MONTH_NAMES.map((name, i) => {
              const ym = `${viewYear}-${String(i + 1).padStart(2, '0')}`;
              const isToday = viewYear === todayYear && i === todayMonth;
              const isStart = startMonth === ym;
              const isEnd = endMonth === ym;
              const inRange = startMonth && endMonth && ym > startMonth && ym < endMonth;
              const disabled = isDisabled(ym);
              const isHoverEnd = showPreview && !disabled && ym === hoverMonth;
              const inHoverRange = showPreview && !disabled && ym > startMonth && ym < hoverMonth;

              return (
                <div
                  key={i}
                  className={`month-cell ${isStart ? 'selected-start' : ''} ${isEnd ? 'selected-end' : ''} ${inRange ? 'in-range' : ''} ${isToday && !isStart && !isEnd && !inRange ? 'current-month' : ''} ${disabled ? 'month-cell-disabled' : ''} ${isHoverEnd && !isEnd ? 'hover-end' : ''} ${inHoverRange ? 'in-hover-range' : ''}`}
                  onClick={() => !disabled && handleMonthClick(i)}
                  onMouseEnter={() => !disabled && setHoverMonth(ym)}
                >
                  {name}
                </div>
              );
            });
            })()}
          </div>
        </div>

        <button
          type="button"
          className="primary-button"
          onClick={(e) => { e.preventDefault(); handleApply(); }}
          disabled={!startMonth || !endMonth}
        >
          Применить
        </button>
      </div>
    </div>
  );
};

export default CalendarModal;
